import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useSelector } from 'react-redux';
import { getFilterOptions, getFooterLinks } from '../../../new-lib/apis';
import ReachUs from '../../../features/contact/components/organic-form/ReachUs';
import FormHeader from '../../../features/contact/components/organic-form/FormHeader';
import FormFooter from '../../../features/contact/components/organic-form/FormFooter';
import {
  setExperimentVariant,
  setIsMobile
} from '../../../redux/actions/appActions';
import { wrapper } from '../../../redux/store';
import dynamic from 'next/dynamic';
import { GetStaticPaths, GetStaticPropsContext } from 'next';
import { StaticPagesUtility } from '../../../new-lib/utilities/static-pages.utility';
import { ConfigsApis } from '../../../new-lib/apis/configs.apis';
import { SCHEMA_TYPES } from '../../../features/structured-data/components/SchemaBuilder';

const ContactFormContainer = dynamic(
  () =>
    import(
      '../../../features/contact/components/organic-form/ContactFormContainer'
    ),
  { ssr: false }
);

export default function Contact() {
  const { t } = useTranslation('contact');

  const isMobile = useSelector((state: any) => state.app.isMobile);

  return (
    <>
      {isMobile ? (
        <>
          <Background></Background>
          <Container>
            <div className="container-fluid">
              <div className="form-container">
                <ContactFormContainer
                  renderedInputs={{
                    name: true,
                    location: true,
                    message: true,
                    mobileNumber: true
                  }}
                  isContactPage={true}
                />
              </div>
              <div className="contact-details">
                <ReachUs />
              </div>
            </div>
          </Container>
        </>
      ) : (
        <Container>
          <div className="container-fluid">
            <Background>
              <div className="headline">
                <h1 className="headline-1">{t('contact_us')}</h1>
              </div>
            </Background>

            <div className="contact-section">
              <div className="contact-details">
                <ReachUs />
              </div>
              <div className="form-container">
                <ContactFormContainer
                  renderedInputs={{
                    name: true,
                    location: true,
                    message: true,
                    mobileNumber: true
                  }}
                  isContactPage={true}
                  layout={{
                    header: <FormHeader hasIcon={false} />,
                    footer: <FormFooter />
                  }}
                />
              </div>
            </div>
          </div>
        </Container>
      )}
    </>
  );
}

const Container = styled.div`
  width: 100%;
  min-height: 70vh;
  .container-fluid {
    .contact-section {
      width: 90%;
      margin: 0 auto;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      position: relative;
      min-height: 400px;
      .form-container {
        width: 540px;
        position: absolute;
        top: -60%;
        inset-inline-end: 0;
      }
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.min}) {
    min-height: unset;
    transform: translateY(100px);
    .container-fluid {
    }
  }
`;

const Background = styled.div`
  position: relative;
  width: 100%;
  height: 367px;
  background-image: url('/assets/images/common/contact-background.webp');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: start;
  border-radius: ${props => props.theme.borderRadius};
  margin-top: 50px;

  .headline {
    position: absolute;
    bottom: 30px;
    inset-inline-start: 60px;
    h1 {
      color: white;
      font-size: ${props => props.theme.font.sizes.xxxl};
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.min}) {
    height: 203px;
    border-radius: 0;
    margin-top: 0;
    position: absolute;
    z-index: -1;
  }
`;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const [footerLinks, configVariables, filterOptions] = await Promise.all([
        getFooterLinks(locale, 'contact'),
        ConfigsApis.getConfigs(locale),
        getFilterOptions(locale)
      ]);

      store.dispatch(setIsMobile(params?.device === 'mobile'));
      store.dispatch(setExperimentVariant(params?.variant as string));

      return {
        revalidate: 60 * 60 * 24,
        props: {
          ...(await serverSideTranslations(locale as string, [
            'contact',
            'common'
          ])),
          configVariables,
          filterOptions: JSON.stringify(filterOptions) || '',
          layout: {
            footerLinks,
            url: `${process.env.NEXT_PUBLIC_HOST}/contact`,
            path: 'contact'
          },
          schemas: [SCHEMA_TYPES.organization]
        }
      };
    }
);

export const getStaticPaths = (async () => {
  return { paths: StaticPagesUtility.get(), fallback: false };
}) satisfies GetStaticPaths;
